import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { Button, Input, IconModal, Passcode } from '@airtm/airtm-ui';
import SelectCountryPhoneCode from 'components/SelectCountryPhoneCode/SelectCountryPhoneCode';
import { PHONE_COUNTRY_CODE_REGEX, PHONE_REGEX } from '@airtm/utils/dist/constants';

const PhoneModal = function ({
  country,
  errorMessage = null,
  errorTitle = '',
  onChange,
  onCountryPhoneCodeChange,
  onHide,
  onSendSMS,
  onSendWhatsApp,
  phone,
  phoneCountryCode,
  countryPhoneRegex,
  show,
  t,
  modalTitle = '',
}) {
  const regexToTest = countryPhoneRegex ? new RegExp(countryPhoneRegex) : PHONE_REGEX;
  const [showPhone, setShowPhone] = useState(phone === '' || phone === null);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    setShowPhone(phone === '' || phone === null);
  }, [show]);

  const handleSubmit = (method) => {
    setFormError('');
    if (!PHONE_COUNTRY_CODE_REGEX.test(phoneCountryCode)) {
      setFormError(
        t('ERRORS:FORM_REQUIRED', { name: t('SETTINGS:CONFIRMATION_LABEL_PHONE_COUNTRY_CODE') }),
      );
      setShowPhone(true);
      return;
    }
    if (!regexToTest.test(phone)) {
      let defaultValues = { min: 6, max: 15 };
      if (countryPhoneRegex) {
        const limits = countryPhoneRegex.toString().match(/\d+/g) ?? [];
        defaultValues =
          limits.length === 1
            ? { number: limits[0] }
            : {
                min: limits[0],
                max: limits[1],
              };
      }

      const exactPhoneLengthText = Object.keys(defaultValues).length === 1 ? '_EXACT_LENGTH' : '';

      setFormError(
        t(`ERRORS:FORM_REGEX_PHONE${exactPhoneLengthText}`, {
          name: t('SETTINGS:CONFIRMATION_LABEL_PHONE'),
          ...defaultValues,
        }),
      );
      setShowPhone(true);
      return;
    }
    if (method === 'SMS') {
      onSendSMS();
    } else {
      onSendWhatsApp();
    }
  };

  return (
    <IconModal modalClose={onHide} show={show}>
      <IconModal.Body
        icon={<Passcode />}
        title={errorMessage ? errorTitle : modalTitle || t('SETTINGS:PHONE_VERIFICATION_TITLE')}
      >
        {errorMessage && <p className="mb-4">{errorMessage}</p>}

        {!showPhone && (
          <p className="mb-4">
            {t('SETTINGS:PHONE_EXPLANATION')}
            <span className="ml-1">{phone?.substring(phone.length - 4)}</span>.
            {!modalTitle && (
              <Button
                onClick={() => {
                  setShowPhone(true);
                }}
                variant="tertiary"
              >
                {t('SETTINGS:PHONE_UPDATE')}
              </Button>
            )}
          </p>
        )}

        {showPhone && (
          <div aria-expanded="false" className="" id="changePhone">
            <div className="p-2 px-sm-6 mb-3">
              <div className="form-group mb-0">
                <SelectCountryPhoneCode
                  country={country}
                  onChangeCountryCode={({ value, phoneRegex }) => {
                    if (formError) {
                      setFormError('');
                    }
                    onCountryPhoneCodeChange({
                      phoneCountryCode: value,
                      countryPhoneRegex: phoneRegex,
                    });
                  }}
                  onChangePhoneNumber={(event) => {
                    onChange('phone', event.target.value);
                  }}
                  phoneCountryCode={phoneCountryCode}
                  phoneNumber={phone}
                  phoneNumberPlaceholder="xxx-xxxx"
                  invalid={!!formError}
                />
                {formError && <Input.HelperText invalid>{formError}</Input.HelperText>}
              </div>
            </div>
          </div>
        )}

        <p className="mb-3">{t('SETTINGS:PHONE_WHATSAPP')}</p>
      </IconModal.Body>
      <IconModal.Actions>
        <Button variant="secondary" onClick={() => handleSubmit('SMS')} data-testid="sms-su">
          {t('SETTINGS:PHONE_SMS_ACTION')}
        </Button>
        <Button
          variant="primary"
          onClick={() => handleSubmit('WHATSAPP')}
          data-testid="whatsapp-su"
        >
          {t('SETTINGS:PHONE_WHATSAPP_ACTION')}
        </Button>
      </IconModal.Actions>
    </IconModal>
  );
};
PhoneModal.propTypes = {
  country: PropTypes.string.isRequired,
  errorMessage: PropTypes.string,
  errorTitle: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onCountryPhoneCodeChange: PropTypes.func.isRequired,
  onHide: PropTypes.func.isRequired,
  onSendSMS: PropTypes.func.isRequired,
  onSendWhatsApp: PropTypes.func.isRequired,
  phone: PropTypes.string.isRequired,
  phoneCountryCode: PropTypes.string.isRequired,
  countryPhoneRegex: PropTypes.string.isRequired,
  show: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  modalTitle: PropTypes.string,
};
export default withTranslation(['SETTINGS', 'ERRORS'])(PhoneModal);
